import { Alert, Button, Form, Input, Modal, Progress, Radio, Select, Upload, UploadFile, UploadProps } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "../../../redux/useAppDispatch";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { InboxOutlined } from "@ant-design/icons";
import Dragger from "antd/es/upload/Dragger";
import { fetchOrganizations, resetOrganizationsTable } from "../../../redux/SettingModule/OrganizationsPage/OrganizationsPageAction";
import { resetOrganizationDetailPage } from "../../../redux/SettingModule/OrganizationDetailPage/OrganizationDetailAction";
import { v4 as uuidv4 } from "uuid";
import { createPack, resetUploadProgress, uploadFile } from "../../../redux/Transcribe/TranscribeAction";
import axios, { CancelTokenSource } from "axios";

type UploadModalProps = {
  close: () => void;
  openState: boolean;
  open: () => void;
};

function UploadModal(props: UploadModalProps) {
  const uploadLoading = useSelector((state: RootState) => state.TranscribeState.createPackLoading);
  const tenantData = useSelector((state: RootState) => state.TranscribeState.pickList.tenantPicklist);
  const uploadProgress = useSelector((state: RootState) => state.TranscribeState.currentFileUpload);
  const cancelUploadRef = useRef<CancelTokenSource | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);

  const newTenantData = [...tenantData.slice(1, tenantData.length)];

  const [enableUpload, setEnableUpload] = useState(false);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadUuid, setUploadUuid] = useState<string | null>(null);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const languagePickList = [
    {
      value: "en-gb",
      label: "English (UK)",
    },
    {
      value: "en-us",
      label: "English (USA)",
    },
  ];
  const isValidFormat = (type: string) => {
    const allowedFormats = ["audio/mpeg", "audio/mp3", "audio/x-m4a", "audio/wav", "audio/x-ms-wma", "audio/flac", "video/mp4", "video/x-msvideo", "video/mpeg", "video/avi", "video/quicktime"];

    return allowedFormats.includes(type);
  };
  const uploadProps: UploadProps = {
    name: "file",

    multiple: false,
    showUploadList: false,

    beforeUpload: (file) => {
      form.resetFields(["InputFileName"]);
      if (!isValidFormat(file.type)) {
        console.error("Invalid file format!");
        setFileList([]);
        return false;
      }
      setFileList((prev) => [file]);
      return false;
    },
  };

  const resetForm = () => {
    form.resetFields();
    setFileList([]);
  };

  useEffect(() => {
    const uploadFileAsync = async () => {
      try {
        const id = uuidv4();
        setUploadUuid(id);
        await dispatch(
          uploadFile({
            data: { IdTenant: form.getFieldValue("IdTenant") },
            file: fileList[0],
            id: id,
            cancelUploadRef: cancelUploadRef,
          })
        ).unwrap();
        setIsFileUploaded(true);
      } catch (error) {
        setIsFileUploaded(false);
        setError("An error occurred while uploading the file.");
      }
    };
    if (fileList.length > 0) {
      uploadFileAsync();
    }
  }, [fileList]);

  //UseEffect
  useEffect(() => {
    return () => {
      dispatch(resetUploadProgress());
      form.resetFields();
      setFileList([]);
      setEnableUpload(false);
      setIsFileUploaded(false);
      setError("");
      cancelUploadRef.current = null;
    };
  }, [props.openState]);

  const UploadModalContent = useMemo(() => {
    const onFinish = async (value: any) => {
      const id = uploadUuid;
      const payload = {
        Uuid: id,
        IdTenant: value.IdTenant,
        JobNumber: value.JobNumber,
        ClientName: value.ClientName,
        TranscriptionType: value.TranscriptionType,
        FileName: fileList[0].name,
        Language: value.Language,
        PackPriority: value.PackPriority,
      };
      try {
        await dispatch(createPack({ data: payload, file: fileList[0], id: id })).unwrap();
        props.close();
        resetForm();
      } catch (error) {
        setError("An error occurred while creating the pack.");
      }
    };

    return (
      <div className="d-flex flex-column justify-content-between gap-4 h-100 w-100">
        <h4>Upload Audio</h4>

        <div className="d-flex flex-column gap-2">
          <Form
            form={form}
            name="createAudioFrom"
            className="d-flex flex-column"
            onChange={() => {
              if (form.getFieldValue("IdTenant") !== undefined && form.getFieldValue("JobNumber") !== undefined && form.getFieldValue("ClientName") !== undefined && form.getFieldValue("TranscriptionType") !== undefined && form.getFieldValue("Language") !== undefined && form.getFieldValue("PackPriority") !== undefined) {
                setEnableUpload(true);
              } else {
                setEnableUpload(false);
              }
            }}
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            onFinish={onFinish}
            initialValues={{ isDummyDomain: false }}
            colon={false}>
            {/* Job Number */}
            <div className="d-flex flex-row flex-1 gap-2">
              <div className="d-flex flex-column gap-3 w-100">
                <Form.Item
                  className="form-item"
                  label="Job Number"
                  name="JobNumber"
                  labelAlign="left"
                  rules={[
                    {
                      required: true,
                      message: "Please provide Job Number.",
                    },
                  ]}>
                  <Input />
                </Form.Item>

                {/* Requestor */}
                <Form.Item
                  className="form-item"
                  label="Requestor"
                  name="ClientName"
                  labelAlign="left"
                  rules={[
                    {
                      required: true,
                      message: "Please provide Requestor.",
                    },
                  ]}>
                  <Input />
                </Form.Item>

                {/* Organization */}
                <Form.Item
                  className="form-item"
                  label="Organization"
                  name="IdTenant"
                  labelAlign="left"
                  required={true}
                  rules={[
                    {
                      required: true,
                      pattern: /^[0-9]*$/,
                      message: "Please provide a valid Organization.",
                    },
                  ]}
                  validateTrigger="onBlur">
                  <Select
                    showSearch
                    optionFilterProp="label"
                    // labelInValue
                    placeholder="Select an Organization"
                    options={newTenantData}>
                    {/* {tenantPickList.map((tenant) => (
                  <Select.Option key={tenant.value} value={tenant.value}>
                    {tenant.label}
                  </Select.Option>
                ))} */}
                  </Select>
                </Form.Item>

                {/* Language */}
                <Form.Item
                  className="form-item"
                  label="Language"
                  name="Language"
                  labelAlign="left"
                  required={true}
                  validateTrigger="onBlur">
                  <Select
                    showSearch
                    options={languagePickList}
                    placeholder="Select an Language"
                    optionFilterProp="children">
                    {/* {tenantPickList.map((tenant) => (
                  <Select.Option key={tenant.value} value={tenant.value}>
                    {tenant.label}
                  </Select.Option>
                ))} */}
                  </Select>
                </Form.Item>

                {/* Audio Type */}
                <Form.Item
                  className="form-item"
                  label="Audio Type"
                  name="TranscriptionType"
                  labelAlign="left"
                  rules={[{ required: true, message: "Please select an Audio Type." }]}>
                  <Radio.Group>
                    <Radio value="Dictation">Dictation</Radio>
                    <Radio value="Transcription">Pure Transcription</Radio>
                  </Radio.Group>
                </Form.Item>

                {/* Priority */}
                <Form.Item
                  className="form-item"
                  label="Priority"
                  name="PackPriority"
                  labelAlign="left"
                  rules={[{ required: true, message: "Please select an Priority." }]}>
                  <Radio.Group>
                    <Radio value="Standard">Standard</Radio>
                    <Radio value="Express">Express</Radio>
                  </Radio.Group>
                </Form.Item>
              </div>
              <div className="w-100 d-flex flex-column">
                <Form.Item
                  className="w-100 h-100"
                  labelCol={{ span: 0 }}
                  wrapperCol={{ span: 24 }}
                  // label="Upload File"
                  getValueFromEvent={(e) => {
                    return e.fileList[0];
                  }}
                  name="InputFileName"
                  labelAlign="left"
                  required={true}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value || !value.type) {
                          return Promise.reject("Please upload a file.");
                        }
                        if (!isValidFormat(value.type)) {
                          return Promise.reject("Invalid file format. Please upload a valid audio or video file (.mp3, .m4a, .mp4, .wav, .wma, .avi, .mpeg, .mov, .flac).");
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}>
                  <Dragger
                    disabled={!enableUpload || form.getFieldError("IdTenant").length > 0}
                    height={385}
                    accept=".mp3, .m4a, .mp4, .wav, .wma, .avi, .mpeg, .mov, .flac"
                    {...uploadProps}>
                    {fileList.length > 0 ? (
                      <div className="d-flex flex-column align-items-center">{fileList[0].name}</div>
                    ) : (
                      <div>
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Click or drag an audio file to this area to upload</p>
                        <p className="ant-upload-hint">
                          Allowed formats:
                          <br />
                          <strong>.mp3,.m4a,.mp4,.wav,.wma,.avi,.mpeg,.mov,.flac</strong>
                        </p>
                      </div>
                    )}
                  </Dragger>
                  {cancelUploadRef.current && (
                    <Progress
                      percent={uploadProgress.uploadProgress}
                      strokeColor="#52c41a"
                      showInfo={true}
                      style={{ margin: "10px" }}
                    />
                  )}
                </Form.Item>
              </div>
            </div>

            {/* Upload */}
            {error && <div className="error justify-content-end align-self-end mb-2">{error}</div>}
            <div className="d-flex gap-3 justify-content-end">
              <Button
                className={`button radius ${!isFileUploaded ? "custom-disabled-look" : ""}`}
                type="primary"
                // disabled={!isFileUploaded}
                loading={uploadLoading}
                htmlType="submit"
                onClick={(e) => {
                  if (!isFileUploaded) {
                    e.preventDefault();
                    return;
                  }
                }}>
                Transcribe
              </Button>
              {/* 
              <Button
                htmlType="reset"
                loading={uploadLoading}
                onClick={() => {
                  props.close();
                  resetForm();
                }}>
                Cancel
              </Button> */}
            </div>
          </Form>
        </div>
      </div>
    );
  }, [form, props, fileList, uploadProgress, enableUpload, uploadUuid, uploadLoading, isFileUploaded, error]);

  return (
    <Modal
      centered
      zIndex={1500}
      width={1000}
      footer={[]}
      onCancel={() => {
        if (cancelUploadRef.current) {
          // Show alert if file is still uploading and have two button close and okay
          let shouldClose = true;

          shouldClose = window.confirm("Are you sure you to cancel the upload?");

          if (shouldClose) {
            cancelUploadRef.current?.cancel("Upload cancelled");
            resetForm();
            setConfirmClose(false);
            props.close();
          }
          // props.close()
          return;
        } else {
          resetForm();
          props.close();
        }
      }}
      open={props.openState}>
      {UploadModalContent}
    </Modal>
  );
}

export default UploadModal;
